import get from 'lodash-es/get';
import map from 'lodash-es/map';
import find from 'lodash-es/find';
import defaultsDeep from 'lodash-es/defaultsDeep';
import cloneDeep from 'lodash-es/cloneDeep';
import isEqual from 'lodash-es/isEqual';
import uniqBy from 'lodash-es/uniqBy';
import LoudnessMeter from './loudnessMeter';
import template from 'lodash-es/template';
import {rescopeFunction} from '../rescopeFunction';
import mediaSettings from './mediaSettings';

const nativeEvents = [
  'dataavailable',
  'error',
  'pause',
  'resume',
  'start',
  'stop',
  ];

export class RecorderAPI {

  constructor(host, optionsPath) {
    this._host = host;
    this._optionsPath = optionsPath;
    this._fileNames='';
    this._recordings = [];
    this._codecs = {
      audio:true,
      video:false
    };
    
    host.on('destroy', () => {
      if(this._recorder){
        this.drop();                                
        delete this._host;
      }
    });

    this.applyOptions();
    host.on('init', () => {
      this.applyOptions();
    });        
  }

  get timestamp(){
    return this._timestamp;
  }

  get sessionTimestamp(){
    return this._sessionTimestamp;
  }

  getBlobOptions(videoAvailable = true){
    return {'type': `${this.getType(videoAvailable)}/${this.getMediaContainerUsed().name}`};
  }

  get recorder() {
    try {
      if (!this._recorderPromise && navigator.mediaDevices) {

        this._recorderPromise = 
        Promise.all([this.getConstraints(),this.getRecorderOptions()])
        .then(([constraints,options])=> {
          if(!constraints.audio&&!constraints.video){
            this.drop();
            throw new Error('NotAvailable');
          }

          if(!this.getMediaContainerUsed().settings[this.getType(constraints.video)+'Ext']){
            console.log(`Container '${this.getMediaContainerUsed().name}' does not support ${this.getType(constraints.video)} file format.`);
            this.drop();
            throw new Error('NotSupported');
          }

          return navigator.mediaDevices
          .getUserMedia(constraints)
          .then((stream) => {
            try {
              const recorder = new MediaRecorder(stream, options);
              if(constraints.audio){
                this._meter = new LoudnessMeter({ stream, onChange:(level)=>this._host.fire('recorderLevel',level)});              
              }

              this._stream = stream;
              let totalSize = 0;
              let startTime = new Date().getTime();
              this._chunkNumber = 1;
              recorder.addEventListener('dataavailable', (e) => {
                this._chunks.push(e.data);
                totalSize+=e.data.size;
                if(!this._restarting && (this.getBufferSize()&&totalSize>this.getBufferSize()||this.getBufferDuration()&&(new Date().getTime()-startTime)>this.getBufferDuration())){
                  console.log('recorder bufferLimit reached');
                  if (this.getAutoUpload() == 'off') {                  
                    this.stop();
                    return;
                  }

                  if(this.getSplitMode()=='binaryChunks'){
                    const rec = {
                      fileName: this.getFileName(),
                      blob: new Blob(this._chunks, this.getBlobOptions(constraints.video)),
                      type: this.getType(constraints.video),
                      timestamp: this.timestamp,
                      sessionTimestamp:this.sessionTimestamp,
                      chunkNumber:this._chunkNumber,
                      
                    };
                    this._recordings.push(rec);
                    rec.generatedName = this.generateFileName(rec.timestamp);
                    this._fileNames.push(rec.generatedName);

                    totalSize=0;
                    startTime = new Date().getTime();                 
                    this.upload(this.timestamp);
                    this._chunkNumber++;
                    this._timestamp = new Date().getTime();
                    this._chunks = [];
                  }else{
                    this._restarting = true;
                    this._recorder.stop();                  
                  }
                }
              });
              recorder.addEventListener('stop', (e) => {
                try {
                  if (this.getAfterRecording() === 'override') {
                    this._recordings = [];
                  }

                  if(this._chunks.length){
                    const rec = {
                      fileName: this.getFileName(),
                      blob: new Blob(this._chunks, this.getBlobOptions(constraints.video)),
                      type: this.getType(constraints.video),
                      timestamp: this.timestamp,
                      sessionTimestamp:this.sessionTimestamp,
                      chunkNumber:this._chunkNumber,
                      
                      final:!this._restart
                    };
                    this._recordings.push(rec);
                    rec.generatedName = this.generateFileName(rec.timestamp);
                    this._fileNames.push(rec.generatedName);
                    // delete this._timestamp;
                    if (this.getAutoUpload() == 'dropbox' || this.getAutoUpload() == 'mput'){
                      this.upload(this.timestamp);
                    }

                    this._chunkNumber++;
                    this._timestamp = new Date().getTime();
                    this._chunks = [];
                    totalSize=0;
                    startTime = new Date().getTime();
                    if(this._restart){
                      this._restarting = false;
                      this._recorder.start(1000);
                    }
                  }
                } catch (error) {
                  this._host.fire('recorderError', this._host, {message:`Error occurred while processing MediaRecorder stop event: ${error.message}`,error, recorder: this});
                }
              });
              nativeEvents.forEach((event) => {
                recorder.addEventListener(event, (e) => {
                  try {
                    this._host.fire(`recorder${event.charAt(0).toUpperCase() + event.slice(1)}`, this._host, {event: e, recorder: this});
                  } catch (error) {
                    throw new Error(`Error occurred while processing MediaRecorder ${event} event: ${error.message}`);
                  }
                });
              });
              this._recorder = recorder;
              return recorder;
            } catch (error) {
              this.drop();
              this._host.fire('recorderError', this._host, {message:`Error occurred while initializing MediaRecorder: ${error.message}`,error, recorder: this});
            }
          })
        .catch((error) => {
          let message='';
          switch(error.name) {
          case 'NotFoundError':
          case 'NotAvailable':
            message = 'No camera/mic found';
            break;
          case 'NotAllowedError':
            message = 'User denied camera/mic access';
            break;
          case 'OverconstrainedError':
            message = "Constraints can't be satisfied by any available device";
            break;
          default:
            message = 'Error accessing camera/mic: ' + error.name;
            break;
          }

          this.drop();

          this._host.fire('recorderError', this._host, {message,error, recorder: this});
        });
  });
  } else if (!this._recorderPromise) {
    this.drop();
    this._host.fire('recorderError', this._host, {recorder: this,message:'Error getting recorder: navigator.mediaDevices is empty or undefined. This could be due to a number of reasons, including the browser not supporting navigator.mediaDevices or there being a security restriction. Please ensure that you are running the application in a compatible browser and that the necessary permissions have been granted.'});
  }

  return this._recorderPromise;
  } catch (error) {
    this.drop();
    this._host.fire('recorderError', this._host, {error,recorder: this,message:`Error occurred while getting recorder: ${error.message}`});
  }
}

applyOptions(){

  const options = get(this._host,this._optionsPath,{});

  if(!this._options || this._options.fileName!==options.fileName){
    this._fileName = options.fileName||'recording';
  }

  if(!this._options || this._options.fileNameFormatter!==options.fileNameFormatter){
    this._fileNameFormatter = options.fileNameFormatter!==undefined?options.fileNameFormatter:'${fileName}';
  }

      // afterRecording option to append or to override
  if(!this._options || this._options.afterRecording!==options.afterRecording){
    this._afterRecording = options.afterRecording||'override';
  }

      //autoUpload option to upload after recording
  if(!this._options || this._options.autoUpload!==options.autoUpload){
    this._autoUpload = options.autoUpload||'off';
  }

  if(!this._options || this._options.bufferSize!==options.bufferSize){
    this._bufferSize = options.bufferSize||0;
  }

  if(!this._options || this._options.bufferDuration!==options.bufferDuration){
    this._bufferDuration = options.bufferDuration||0;
  }

  if(!this._options || this._options.splitMode!==options.splitMode){
    this._splitMode = options.splitMode||'separateFiles';
  }    

      // afterUpload option to keep recording or remove it
  if(!this._options || this._options.afterUpload!==options.afterUpload){
    this._afterUpload = options.afterUpload||'remove';
  }

      //dropBoxName
  if(!this._options || this._options.dropBoxName!==options.dropBoxName){
    this._dropBoxName = options.dropBoxName||'';
  }

      //dropBoxPath
  if(!this._options || this._options.dropBoxPath!==options.dropBoxPath){
    this._dropBoxPath = options.dropBoxPath||'';
  }

  if(!this._options || !isEqual(this._options.audio,options.audio)){
    this.setAudio(options.audio);        
  }

  if(!this._options || !isEqual(this._options.video,options.video)){
    this.setVideo(options.video);
  }

  const mcArray = [];
  if(options.mediaContainer){
      //issue with DeltaPlane - returns invalid arrays
    for(let i=0;options.mediaContainer[i];i++){
      mcArray[i]=options.mediaContainer[i];
    }      
  }

  if(!mcArray.length){
    mcArray.push({type:'webm'});
  }

  if(!this._options || !isEqual(this._mcArray,mcArray)){
    this.setMediaContainer(mcArray);
    this._mcArray = mcArray;
  }    

  this._options = options;
  this._host.fire('recorderOptionsChange', this._host, {options, recorder: this});
}

getRecording(timestamp){
  return timestamp?this._recordings.find(r=>r.timestamp==timestamp):(this._recordings.length?this._recordings[this._recordings.length-1]:undefined);
}

replay(timestamp=0){
  const rec = this.getRecording(timestamp);
  if(rec){
    if(!this._audioPlayer){
      const url = URL.createObjectURL(rec.blob);
      this._audioPlayer = new Audio(url);
      this._audioPlayer.play();
      this._audioPlayer.addEventListener('ended', () => {
        URL.revokeObjectURL(url);
        delete this._audioPlayer;
      });

      this._audioPlayer.addEventListener('pause', () => {
        URL.revokeObjectURL(url);
        delete this._audioPlayer;
      });
    }else{
      this._audioPlayer.pause();        
    }
  }
}

download(timestamp=0){
  const rec = this.getRecording(timestamp);
  if(rec){      
    const a = document.createElement('a');

    a.setAttribute('href', URL.createObjectURL(rec.blob));
    a.setAttribute('download', this.generateFileName(timestamp));
    a.style.display='none';
    document.body.appendChild(a);  
    a.click();
    a.remove();
  }    
}

getFileNames(){
  return this._fileNames;
}

generateFileName(timestamp=0){
  const rec = this.getRecording(timestamp);
  if(!rec) {return;}

  const {fileName,type,chunkNumber, sessionTimestamp, final} = rec;
  const ext = this.getMediaContainerUsed().settings[type+'Ext'];

  let formatter = this.getFileNameFormatter()||'${fileName}';

  let res;

  if(typeof formatter == 'string'){
    if(!formatter.endsWith('.${ext}')){
      formatter+='.${ext}';
    }

    if(!this._formatter||this._formatter.src!=formatter){
      this._formatter = template(formatter);              
      this._formatter.src = formatter;
    }

    res = this._formatter({
      fileName,timestamp, chunkNumber,sessionTimestamp,final, ext, type, control:this._host, component:this._host.instance||this._host, parentControl:this._host.parentControl
    });

  }else{
    if(!this._formatter||this._formatter._original!=formatter){
      const hostInstance = this._host.instance;
      const scope = {
        fileName,
        timestamp,
        chunkNumber,
        sessionTimestamp,
        final,
        ext,
        type:rec.type,
        control:this._host,
        component:hostInstance||this._host,
        parentControl:this._host.parentControl,
        chart:hostInstance && hostInstance.chart,
        dataSource:hostInstance && hostInstance.getDataSource()
      };
      Object.defineProperty(scope, 'data', {
        get() { return hostInstance && hostInstance.data; },
        enumerable: true
      });
      Object.defineProperty(scope, 'fn', {
        get() { return hostInstance && hostInstance.fn; },
        enumerable: true
      });
      this._formatter = rescopeFunction(this._formatter||formatter, scope, this._host);
    }

    try{
      res = this._formatter(fileName, timestamp, chunkNumber, sessionTimestamp, final, ext, rec.type, this._host);
      if(!res.endsWith('.'+ext)){
        res+='.'+ext;
      }
    }catch(ex){
      res = 'Formatter error: ' + ex.message||ex;
      console.error(ex);
    }
  }

  return res;    
}

upload(timestamp=0){
  return this.dropBoxUpload(timestamp);
}



dropBoxUpload(timestamp=0, dropBoxPath=this.getDropBoxPath(), dropBoxName=this.getDropBoxName()){
  const rec = this.getRecording(timestamp);
  if(!rec) {return;}

  if(!dropBoxName){
    throw new Error('dropBoxName is not defined');
  }

  if(!dropBoxPath){
    throw new Error('dropBoxPath is not defined');
  }

  const path = `${dropBoxName}/${dropBoxPath}`;

  const file = new File([rec.blob], this.generateFileName(timestamp));



  let resp = this.getAutoUpload()=='dropbox'?
    this._host.dropBoxUpload(path, file):
    this._host.dropBoxMput(
      path, 
      file, 
      this.getSplitMode()=='separateFiles'?'AUDIO':'BINARY', 
      rec.chunkNumber,
      rec.final
    );

  if(this.getAfterUpload()==='remove'){
    resp = resp.then((r) => {
      this._recordings = this._recordings.filter(r=>r.timestamp!==timestamp);
      return r;
    });
  }

  resp = resp.then((r) => {
    console.log({r});
    this._host.fire('recorderUploadComplete', this._host, {response: r, recorder: this, target: 'dropbox'});
    return r;
  });

  return resp;
}

drop(){
  if(this._recorder){
    this.stop();            
    delete this._recorder;      
  }

  if(this._recorderPromise){
    delete this._recorderPromise;      
  }

  if(this._meter){
    this._meter.drop();
    delete this._meter;        
  }
}

getDropBoxPath(){
  return this._dropBoxPath;
}

setDropBoxPath(dropBoxPath=''){
  this._dropBoxPath = dropBoxPath;
}

getDropBoxName(){
  return this._dropBoxName;
}

setDropBoxName(dropBoxName=''){
  this._dropBoxName = dropBoxName;
}

getMediaContainerUsed(){
  return this._mediaContainerUsed||{};
}

getMediaContainer(){
  return this._mediaContainer;
}

setMediaContainer(mediaContainer){
  if(!isEqual(this._mediaContainer,mediaContainer)){
    this._mediaContainer =  mediaContainer;
    this._mediaContainerUsed = chooseAvailableContainer(cloneDeep(mediaContainer), this.getAudio().enabled, this.getVideo().enabled);
    delete this._videoNorm;
    delete this._audioNorm;
  }
}


setAudio(audio){
  if(!isEqual(this._audio,audio)){
    this.drop();
    this._audio = audio;
    delete this._audioNorm;
  }
}

getAudio(){
  if(!this._audioNorm){
    this._audioNorm = defaultsDeep({},this._audio,{enabled:true,quality:'medium'});
  }

  return this._audioNorm;
}

setVideo(video){
  if(!isEqual(this._video,video)){
    this.drop();
    this._video = video;      
    delete this._videoNorm;
  }
}

getVideo(){
  if(!this._videoNorm){
    this._videoNorm = defaultsDeep({},this._video,{enabled:false,quality:'medium',orientation:'auto'});
    if(this._videoNorm.orientation=='auto'){
      this._videoNorm.orientation = window.screen.width > window.screen.height ? 'horizontal' : 'vertical'
    }      
  }

  return this._videoNorm;
}

getType(videoAvailable = true){    
  return videoAvailable && this.getVideo().enabled && this.getMediaContainerUsed().video ? 'video' : 'audio';
}

setAfterRecording(afterRecording){
  this._afterRecording = afterRecording;
}

getAfterRecording(){
  return this._afterRecording;
}

setAfterUpload(afterUpload){
  this._afterUpload = afterUpload;
}

getAfterUpload(){
  return this._afterUpload;
}

setAutoUpload(autoUpload){
  this._autoUpload = autoUpload;
}

getAutoUpload(){
  return this._autoUpload;
}

setBufferSize(bufferSize){
  this._bufferSize = bufferSize;
}

getBufferSize(){
  return this._bufferSize;
}

setBufferDuration(bufferDuration){
  this._bufferDuration = bufferDuration;
}

getBufferDuration(){
  return this._bufferDuration;
}

setSplitMode(splitMode){
  this._splitMode = splitMode;
}

getSplitMode(){
  return this._splitMode;
}

setFileName(fileName){
  this._fileName = fileName;
}

getFileName(){
  return this._fileName;
}

setFileNameFormatter(fileNameFormatter){
  this._fileNameFormatter = fileNameFormatter;
}

getFileNameFormatter(){
  return this._fileNameFormatter;
}  

getConstraints() {
  let constraints = {};
  const container = this.getMediaContainerUsed();
  const audio = this.getAudio();
  const video = this.getVideo();    


  constraints.audio = audio.enabled && !!container.audio.codec;

  if (video.enabled && container.video.codec) {
    let width, height;      

    if (container.video.width) {
      width = container.video.width;
    } else  {
      width = Math.round(parseInt(container.video.codec.quality[video.quality].resolution)* 1.778);
    }

    if (container.video.height) {
      height = container.video.height;
    } else  {
      height = parseInt(container.video.codec.quality[video.quality].resolution);
    }

    if (video.orientation === 'vertical') {
      [width, height] = [height, width];
    }

    constraints.video = {
      width: { ideal: width },
      height: { ideal: height }
    };

  } else {
    constraints.video = false;
  }

  const promises = [];

  if(constraints.audio){
    promises.push(testDeviceAvailability('audio').then(a => {
      constraints.audio = a;
    }));
  }

  if(constraints.video){
    promises.push(testDeviceAvailability('video').then(v => {
      if(!v){
        constraints.video = false;
      }
    }));
  }

  return Promise.all(promises).then(() => constraints);
}

getRecorderOptions() {
  const container = this.getMediaContainerUsed();
  let audio = this.getAudio();
  let video = this.getVideo();

  const promises = [];

  if(audio.enabled){
    promises.push(testDeviceAvailability('audio').then(a => {
      if(!a){
        audio = {...audio,enabled:false};
      }
    }));
  }

  if(video.enabled){
    promises.push(testDeviceAvailability('video').then(v => {
      if(!v){
        video = {...video,enabled:false};
      }
    }));
  }

  return Promise.all(promises).then(() => {

    const codecs = [];
    if(audio.enabled&&container.audio.codec){
      codecs.push(container.audio.codec.name);
    }

    if(video.enabled&&container.video.codec){
      codecs.push(container.video.codec.name);
    }

    let options = {
      mimeType: `${this.getType(video.enabled)}/${container.name}; codecs="${codecs}"`
    };

    const audioBitsPerSecond = audio.enabled ? container.audio.bitrate|| container.audio.codec && container.audio.codec.quality[audio.quality].bitrate : undefined;

    if(audioBitsPerSecond){
      options.audioBitsPerSecond = audioBitsPerSecond;
    }

    const videoBitsPerSecond = video.enabled ? container.video.bitrate|| container.video.codec && container.video.codec.quality[video.quality].bitrate : undefined;

    if(videoBitsPerSecond){
      options.videoBitsPerSecond = videoBitsPerSecond;
    }

    return options;
  });
}

start(){
  this.applyOptions();
  return this.recorder.then(recorder => {
    if(recorder){                
      try{
        this._timestamp = new Date().getTime();
        this._sessionTimestamp = this._timestamp;
        this._chunks = [];
        this._chunkNumber=1;
        this._fileNames=[];

        if(this.getBufferSize()||this.getBufferDuration()){
          recorder.start(1000);
          this._restart = true;
        }else{              
          recorder.start();
        }

        if(this._meter){
          this._meter.start();            
        }
      }catch(e){
        console.log('Cannot start recorder');
        console.error(e);
      }
    }else{
      if(!this._mediaContainerUsed){
        console.log('No supported media comtainers found');
      }
    }
  });
}

pause(){
  this.getState()=='recording'&&this._recorder.pause();
}

resume(){
  this.getState()=='paused'&&this._recorder.resume();
}

stop(){
  this._restart = false;
  if(this.getState()!=='inactive'){
    this._recorder.stop();
    if(this._stream){
      this._stream.getTracks().forEach(track => track.stop());
      delete this._stream;
      delete this._recorderPromise;
    }
  }

  if(this._meter){
    this._meter.stop();      
  }
}

requestData(){
  this._recorder&&this._recorder.requestData();        
}    

getState(){
  return this._recorder&&this._recorder.state||'inactive';
}

}




export function chooseAvailableContainer(preferredContainers, audio, video) {  

  // preferredContainers = map(preferredContainers,
  //   pc=> find(mediaSettings.list,
  //     s=>s.name==pc.type));

  let filteredContainers = video
  ? [...preferredContainers,...mediaSettings.list.filter(container => container.video).map(c=>({type:c.name}))]
  : [...preferredContainers,...mediaSettings.list.map(c=>({type:c.name}))];  

  
  filteredContainers = uniqBy(filteredContainers,'type');


  let container;

  for (container of filteredContainers) {    
    const type = video&&mediaSettings[container.type].video?'video':'audio';
    const mediaRecorderOptions = { mimeType: `${type}/${container.type}` };
    const isSupported = MediaRecorder.isTypeSupported(mediaRecorderOptions.mimeType);
    if (isSupported) {
      container.name  = container.type;
      container.settings = mediaSettings[container.type];

      defaultsDeep(container,{audio:{},video:{}});
      let audioCodec = audio;
      if(audioCodec && container.audio && container.audio.codec){
        audioCodec = container.audio.codec;
      }

      let videoCodec = video;
      if(videoCodec && container.video && container.video.codec){
        videoCodec = container.video.codec;
      }

      const codecs = chooseAvailableCodecs(container.settings, audioCodec,videoCodec);
      container.audio.codec = codecs.audio;
      container.video.codec = codecs.video;      
      break;
    }

    container = null;
  }

  for(const pc of preferredContainers){
    if(!pc) {continue;}

    if(pc.type==container.type){
      console.log(`Container ${container.type} selected.`);
      break;
    }

    console.log(`Container ${pc.type} is not supported, trying to fallback..`)
  }

  return container;
}




export function chooseAvailableCodecs(container, preferredAudioCodec, preferredVideoCodec) {
  if(typeof container == 'string'){
    container = mediaSettings.list.find(s=>s.name==container);
  }

  if(preferredAudioCodec=='auto'){preferredAudioCodec=true;}

  if(preferredVideoCodec=='auto'){preferredVideoCodec=true;}

  if(preferredAudioCodec=='off'){preferredAudioCodec=false;}

  if(preferredVideoCodec=='off'){preferredVideoCodec=false;}


  let audio;
  if(preferredAudioCodec===true){
    audio = container.audio[0];
  }else if(preferredAudioCodec!==false){
    audio = container.audio.find(a=>a.name==preferredAudioCodec);
    if(!audio){
      audio = container.audio[0];
      console.warn(`Audio codec ${preferredAudioCodec} is not compatible with container ${container.name}, fallback to ${audio.name}.`);
    }
  }

  let video=false;
  if(!container.video&&preferredVideoCodec){
    console.warn(`Container ${container.name} does not support video.`);
  }else if(preferredVideoCodec===true){
    video = container.video[0];
  }else if(preferredVideoCodec!==false){
    video = container.video.find(a=>a.name==preferredVideoCodec);
    if(!video){
      video = container.video[0];
      console.warn(`Video codec ${preferredVideoCodec} is not compatible with container ${container.name}, fallback to ${video.name}.`);
    }
  }

  const type = video ?'video':'audio';

  const audioCodecs = uniqBy([audio, ...container.audio],c => c&&c.name);
  const videoCodecs = container.video ? uniqBy([video, ...container.video],c => c&&c.name) : [];
  let videoCodec;
  let audioCodec;
  let isSupported;
  for (videoCodec of videoCodecs) {
    for (audioCodec of audioCodecs) {
      const codecs = [audioCodec, videoCodec].filter(Boolean);
      const mimeType = `${type}/${container.name}${codecs.length ? `; codecs="${codecs.map(c=>c.name).join(', ')}"` : ''}`;
      isSupported = MediaRecorder.isTypeSupported(mimeType);
      if (isSupported||!audio){
        break;
      }
    }

    if(isSupported||!video) {
      break;
    }
  }

  if(audio&&(!audioCodec||audio.name!==audioCodec.name)){
    if(!audioCodec){
      console.log(`No audio codecs compatible with ${container.name} found`);
    }else{
      console.log(`${audio.name} codec is not supported by browser, fallback to ${audioCodec.name}`);
    }
  }

  if(video&&(!videoCodec||video.name!==videoCodec.name)){
    if(!videoCodec){
      console.log(`No video codecs compatible with ${container.name} found`);
    }else{
      console.log(`${video.name} codec is not supported by browser, fallback to ${videoCodec.name}`);
    }
  }

  return {audio:audioCodec,video:videoCodec};
}


function testDeviceAvailability(kind) {
  // if (testDeviceAvailability[kind] !== undefined) {
  //   return Promise.resolve(testDeviceAvailability[kind]);
  // }

  const mediaType = kind === 'audio' ? 'Microphone' : 'Camera';

  return navigator.mediaDevices.getUserMedia({ [kind]: true })
    .then((stream) => {
      stream.getTracks().forEach(track => track.stop());
      // testDeviceAvailability[kind] = true;
      return true;
    })
    .catch((err) => {
      console.log(`${mediaType} not available:`, err.name, '-', err.message);
      // testDeviceAvailability[kind] = false;
      return false;
    });
}
